import { useEffect, useMemo, useState } from "react";
import { adminApi, type SidebarItem } from "@/services/adminApi";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Layout,
  Loader2,
  RotateCcw,
  Save,
  X,
} from "lucide-react";
import { toast } from "sonner";

const SIDEBAR_ITEM_KEYS = [
  "home",
  "arc",
  "workflows",
  "tasks",
  "emailAssistant",
  "meetings",
] as const;

type SidebarItemKey = (typeof SIDEBAR_ITEM_KEYS)[number];

const ITEM_METADATA: Record<SidebarItemKey, { label: string; description: string }> = {
  home: {
    label: "Home",
    description: "Main landing page for every user.",
  },
  arc: {
    label: "Arc",
    description: "The Arc workspace entry point.",
  },
  workflows: {
    label: "Workflows",
    description: "Workflow builder and library — gated by the `workflows` feature flag.",
  },
  tasks: {
    label: "Tasks",
    description: "Task management — gated by the `tasks` feature flag.",
  },
  emailAssistant: {
    label: "Email Assistant",
    description: "Email triage and drafting — gated by the `emailAssistant` feature flag.",
  },
  meetings: {
    label: "Meetings",
    description:
      "Meeting recordings — visible when `meetingBot` (online) or `ambientRecording` (offline) is enabled.",
  },
};

const VALID_KEY_SET = new Set<string>(SIDEBAR_ITEM_KEYS);

function reconcile(items: SidebarItem[]): SidebarItem[] {
  const seen = new Set<string>();
  const result: SidebarItem[] = [];
  for (const it of items) {
    if (!VALID_KEY_SET.has(it.key) || seen.has(it.key)) continue;
    result.push({ key: it.key, visible: it.visible });
    seen.add(it.key);
  }
  for (const key of SIDEBAR_ITEM_KEYS) {
    if (!seen.has(key)) result.push({ key, visible: true });
  }
  return result;
}

function itemsEqual(a: SidebarItem[], b: SidebarItem[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].key !== b[i].key || a[i].visible !== b[i].visible) return false;
  }
  return true;
}

export default function SidebarConfigPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serverItems, setServerItems] = useState<SidebarItem[]>([]);
  const [items, setItems] = useState<SidebarItem[]>([]);

  useEffect(() => {
    adminApi
      .getSidebarConfig()
      .then((res) => {
        const reconciled = reconcile(res.items);
        setServerItems(reconciled);
        setItems(reconciled);
      })
      .catch((e: any) =>
        toast.error(
          e?.response?.data?.error ||
            e?.message ||
            "Failed to load sidebar config",
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  const isDirty = useMemo(() => !itemsEqual(items, serverItems), [items, serverItems]);

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  };

  const toggleVisible = (index: number) => {
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, visible: !it.visible } : it)),
    );
  };

  const handleReset = () => setItems(serverItems);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.updateSidebarConfig(items);
      const reconciled = reconcile(res.items);
      setServerItems(reconciled);
      setItems(reconciled);
      toast.success("Sidebar configuration saved");
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error || e?.message || "Failed to save sidebar config",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const visibleCount = items.filter((it) => it.visible).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Layout className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Sidebar Configuration
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Reorder and toggle top-level web sidebar tabs · {visibleCount}/
              {items.length} visible
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={!isDirty || saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Changes apply on the user&apos;s next login or page refresh — no live push.
        Feature-flag gates still apply on top of these settings (e.g. hiding
        Workflows here removes it; turning off the `workflows` flag also removes
        it).
      </p>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden text-sm">
        {/* Head */}
        <div className="grid grid-cols-[60px_1fr_120px_120px] bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          <div className="px-4 py-2.5 text-center">#</div>
          <div className="px-4 py-2.5">Item</div>
          <div className="px-4 py-2.5 border-l border-gray-200 text-center">
            Visibility
          </div>
          <div className="px-4 py-2.5 border-l border-gray-200 text-center">
            Order
          </div>
        </div>

        {/* Rows */}
        {items.map((it, index) => {
          const meta = ITEM_METADATA[it.key as SidebarItemKey];
          if (!meta) return null;
          const isFirst = index === 0;
          const isLast = index === items.length - 1;
          return (
            <div
              key={it.key}
              className="grid grid-cols-[60px_1fr_120px_120px] border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
            >
              {/* Position */}
              <div className="px-4 py-3.5 flex items-center justify-center text-sm font-medium text-gray-400">
                {index + 1}
              </div>

              {/* Item info */}
              <div className="px-4 py-3.5">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                      it.visible ? "bg-green-500" : "bg-gray-300"
                    }`}
                  />
                  <span className="text-sm font-medium text-gray-900">
                    {meta.label}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 ml-4">
                  {meta.description}
                </p>
              </div>

              {/* Visibility toggle */}
              <div className="flex items-center justify-center border-l border-gray-100">
                <button
                  type="button"
                  onClick={() => toggleVisible(index)}
                  disabled={saving}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide
                    transition-all duration-150 cursor-pointer
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${
                      it.visible
                        ? "bg-green-50 text-green-700 hover:bg-green-100"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }
                  `}
                >
                  {it.visible ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                  {it.visible ? "ON" : "OFF"}
                </button>
              </div>

              {/* Order controls */}
              <div className="flex items-center justify-center gap-1 border-l border-gray-100">
                <button
                  type="button"
                  onClick={() => moveItem(index, -1)}
                  disabled={isFirst || saving}
                  aria-label={`Move ${meta.label} up`}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, 1)}
                  disabled={isLast || saving}
                  aria-label={`Move ${meta.label} down`}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
